import React, { useEffect, useRef, useState } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SecureConnected from "../components/SecureConnected";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import useScrollReveal from "../hooks/useScrollReveal";
import "./PartnerApplication.css";

const TURNSTILE_SCRIPT_ID = "trilloan-turnstile-script";

function PartnerApplicationTurnstile({ onToken, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return undefined;

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: "partner_application",
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken("")
      });
    };

    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", renderWidget);
    renderWidget();

    return () => {
      script.removeEventListener("load", renderWidget);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onToken, resetKey, siteKey]);

  if (!siteKey) {
    return <p className="partner-turnstile-error">Security verification is not configured. Please contact Corporate@trilloan.com.</p>;
  }

  return <div className="partner-turnstile" ref={containerRef} aria-label="Security verification" />;
}

export default function PartnerApplication() {
  useScrollReveal();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setResult(null);

    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries());
    payload.consent = form.get("consent") === "on";
    payload.turnstile_token = turnstileToken;

    try {
      const endpoint = process.env.REACT_APP_PARTNER_APPLICATION_API_URL || "/api/v1/tickets/partner-applications";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.status) throw new Error(data.error || "Unable to submit your application.");

      event.currentTarget.reset();
      setResult({ type: "success", message: `Application submitted. Reference: ${data.result.reference_number}` });
    } catch (error) {
      setResult({ type: "error", message: error.message || "Unable to submit your application." });
    } finally {
      setSubmitting(false);
      setTurnstileToken("");
      setTurnstileResetKey((key) => key + 1);
    }
  };

  return (
    <main className="partner-application-page">
      <SiteHeader />

      <section className="partner-application-section tl-reveal">
        <div className="partner-application-heading">
          <h1>Ready to Partner?</h1>
          <p>
            Fill out the form below and our partnerships team will review your
            application and contact you shortly.
          </p>
          <span>Prefer to reach out directly?</span>
          <a href="mailto:Corporate@trilloan.com">
            <EmailOutlinedIcon />
            Corporate@trilloan.com
          </a>
        </div>

        <form className="partner-form" onSubmit={handleSubmit}>
          <div className="partner-form-banner">
            <h2>Set a meeting with us!</h2>
            <p>Tell us about yourself and the business you represent, and we&apos;ll get back to you shortly.</p>
          </div>

          <div className="partner-form-body">
            <h3>Business Information</h3>
            <div className="partner-form-grid">
              <label>
                Business Name *
                <input name="business_name" type="text" placeholder="Enter your business name" required maxLength={200} />
              </label>
              <label>
                Business Email Address *
                <input name="business_email" type="email" placeholder="your.email@business.com" required maxLength={254} />
              </label>
              <label>
                Business Type *
                <select name="business_type" defaultValue="" required>
                  <option value="" disabled>
                    Select a Business
                  </option>
                  <option value="Corporation">Corporation</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                </select>
              </label>
              <label>
                Nature of Business *
                <select name="nature_of_business" defaultValue="" required>
                  <option value="" disabled>
                    Select a Nature
                  </option>
                  <option>Lending</option>
                  <option>Financial Services</option>
                  <option>Retail</option>
                  <option>Other</option>
                </select>
              </label>
            </div>

            <label>
              Business Address *
              <input name="business_address" type="text" placeholder="Enter your business address" required maxLength={500} />
            </label>

            <div className="partner-form-grid">
              <label>
                Website Link <span>(Optional)</span>
                <input name="website_link" type="url" placeholder="Enter your website link" maxLength={500} />
              </label>
              <label>
                Social Media Link <span>(Optional)</span>
                <input name="social_media_link" type="url" placeholder="Enter your social media link" maxLength={500} />
              </label>
            </div>

            <h3>Contact information</h3>
            <div className="partner-form-grid">
              <label>
                First Name *
                <input name="first_name" type="text" placeholder="Enter your First name" required maxLength={100} />
              </label>
              <label>
                Last Name *
                <input name="last_name" type="text" placeholder="Enter your Last name" required maxLength={100} />
              </label>
              <label>
                Mobile Number *
                <input name="mobile_number" type="tel" placeholder="Enter your mobile number" required maxLength={20} />
              </label>
              <label>
                Email Address *
                <input name="email_address" type="email" placeholder="your@email.com" required maxLength={254} />
              </label>
              <label>
                Position and Department *
                <input name="position_department" type="text" placeholder="Enter your Position and Department" required maxLength={200} />
              </label>
              <label>
                Office Number *
                <input name="office_number" type="tel" placeholder="Enter your Office Number" required maxLength={20} />
              </label>
            </div>

            <label>
              Tell Us About Your Business and Intention
              <textarea name="intention" maxLength={5000} placeholder="Share your business details, goals, and why you'd like to partner with trilLOAN....." />
            </label>

            <label className="partner-consent">
              <input name="consent" type="checkbox" required />
              <span>
                I agree to trilLOAN's Terms of Service and Privacy Policy. I also
                consent to being contacted regarding this partnership inquiry.
              </span>
            </label>

            <input className="partner-form-honeypot" name="fax_number" type="text" tabIndex="-1" autoComplete="off" aria-hidden="true" />

            <PartnerApplicationTurnstile onToken={setTurnstileToken} resetKey={turnstileResetKey} />

            {result && <p className={`partner-form-result partner-form-result--${result.type}`} role="status">{result.message}</p>}

            <button type="submit" disabled={submitting || !turnstileToken}>
              {submitting ? "Submitting..." : "Submit Application"}
              <ArrowForwardRoundedIcon />
            </button>
          </div>
        </form>
      </section>

      <SecureConnected />
      <SiteFooter />
    </main>
  );
}
